// src/pages/reports/TallySheetReport.js
import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../components/Layout";
import { db } from "../../firebase";

import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { Timestamp } from "firebase/firestore";

function formatDDMMYYYY(ymd) {
  if (!ymd) return "";
  const [yyyy, mm, dd] = ymd.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

function normalizeDate(value) {
  if (!value) return "";

  // Firestore Timestamp
  if (value.seconds) {
    return new Date(value.seconds * 1000)
      .toISOString()
      .slice(0, 10);
  }

  // JS Date
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  // Already string (YYYY-MM-DD)
  return String(value);
}


function toStartOfDayTs(dateStr) {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(d);
}

function toEndOfDayTs(dateStr) {
  const d = new Date(dateStr);
  d.setHours(23, 59, 59, 999);
  return Timestamp.fromDate(d);
}



function formatNumber(v) {
  return Number(v || 0).toFixed(2);
}

function today() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function TallySheetReport() {
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [searchText, setSearchText] = useState("");

  const [loading, setLoading] = useState(false);
  const [allRows, setAllRows] = useState([]); 
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const [totals, setTotals] = useState({
    Qty: 0,
    BasicAmount: 0,
    TaxAmount: 0,
    AddlCessAmount: 0,
    ewayAmount: 0,
    RebateAmount: 0,
    GrandTotal: 0,
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (opts = {}) => {
    try {
      setLoading(true);

      const from = opts.fromDate || fromDate;
      const to = opts.toDate || toDate;

      const qRef = collection(db, "orders");
      const q = query(
  collection(db, "orders"),
  where("status", "==", "APPROVED"),
  where("approvedAt", ">=", toStartOfDayTs(from)),
  where("approvedAt", "<=", toEndOfDayTs(to)),
  orderBy("approvedAt", "asc")
);


      const snap = await getDocs(q);

      const map = new Map();

      snap.docs.forEach((docSnap) => {
        const o = docSnap.data();
        const date = normalizeDate(o.approvedAt);

        const lines = Array.isArray(o.orderLines) ? o.orderLines : [];

        lines.forEach((L) => {
          const key = `${date}__${L.brand}`;

          if (!map.has(key)) {
            map.set(key, {
              date,
              Rate: Number(L.rate || 0), 
              Brand: L.brand || "",
              Qty: 0,
              BasicAmount: 0,
              TaxAmount: 0, 
              AddlCessAmount: 0,
              ewayAmount: 0,
              RebateAmount: 0,
              GrandTotal: 0,
            });
          }

          const g = map.get(key);

          const qty = Number(L.qty || 0);
          const basicAmount = Number(L.basicAmount || 0);
          const gstAmount = Number(L.gstAmount || 0);
          const cessAmount = Number(L.cessAmount || 0);
          const taxAmount = gstAmount + cessAmount;
          const addlCessAmount = Number(L.addlCessAmount || 0);
          const ewayAmount = Number(L.ewayAmount || 0);
          const rebateAmount = Number(L.rebateAmount || 0);
          const grandTotal = ewayAmount + rebateAmount; // tally sheet adds rebate

          g.Qty += qty;
          g.BasicAmount += basicAmount;
          g.TaxAmount += taxAmount;
          g.AddlCessAmount += addlCessAmount;
          g.ewayAmount += ewayAmount;
          g.RebateAmount += rebateAmount;
          g.GrandTotal += grandTotal;
        });
      });

      const rows = Array.from(map.values()).sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
        return a.Brand.localeCompare(b.Brand);
      });

      setAllRows(rows);
      computeTotals(rows);
      setPage(1);

    } catch (err) {
      console.error(err);
      alert("Error loading tally sheet");
    } finally {
      setLoading(false);
    }
  };

  const computeTotals = (rows) => {
    const t = {
      Qty: 0,
      BasicAmount: 0,
      TaxAmount: 0,
      AddlCessAmount: 0,
      ewayAmount: 0,
      RebateAmount: 0,
      GrandTotal: 0,
    };

    rows.forEach((r) => {
      t.Qty += Number(r.Qty || 0);
      t.BasicAmount += Number(r.BasicAmount || 0);
      t.TaxAmount += Number(r.TaxAmount || 0);
      t.AddlCessAmount += Number(r.AddlCessAmount || 0);
      t.ewayAmount += Number(r.ewayAmount || 0);
      t.RebateAmount += Number(r.RebateAmount || 0);
      t.GrandTotal += Number(r.GrandTotal || 0);
    });

    setTotals(t);
  };

  const visibleRows = useMemo(() => {
    const txt = (searchText || "").toLowerCase();
    const filtered = allRows.filter((r) => {
      if (!txt) return true;

      return (
        (r.Brand || "").toLowerCase().includes(txt) ||
        (r.date || "").toLowerCase().includes(txt)
      );
    });

    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [allRows, searchText, page]);

  const exportExcel = () => {
    const rows = allRows.map((r) => ({
      Date: r.date,
      Rate: r.Rate,
      Brand: r.Brand,
      Qty: r.Qty,
      BasicAmount: r.BasicAmount,
      TaxAmount: r.TaxAmount,
      AddlCessAmount: r.AddlCessAmount,
      ewayAmount: r.ewayAmount,
      RebateAmount: r.RebateAmount,
      GrandTotal: r.GrandTotal,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TallySheet");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout]), `TallySheet_${fromDate}_to_${toDate}.xlsx`);
  };

  return (
    <Layout>
      <div style={{ padding: 20 }}>
        <h2>Tally Sheet Report</h2>

        <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "center" }}>
          <div>
            <label>From: </label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <label>To: </label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <button onClick={() => loadData({ fromDate, toDate })}>Apply</button>

          <input
            placeholder="Search brand/date..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ marginLeft: 12, padding: 6, minWidth: 250 }}
          />

          <button onClick={exportExcel} style={{ marginLeft: "auto" }}>
            Export Excel
          </button>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table border="1" cellPadding="8" style={{ width: "100%", minWidth: 1100 }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Rate(BasicEway)</th>
                <th>Brand</th>
                <th>Qty</th>
                <th>BasicAmount</th>
                <th>TaxAmount</th>
                <th>AddlCessAmount</th>
                <th>ewayAmount</th>
                <th>RebateAmount</th>
                <th>GrandTotal</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr><td colSpan="10">Loading...</td></tr>
              ) : visibleRows.length === 0 ? (
                <tr><td colSpan="10">No data</td></tr>
              ) : (
                visibleRows.map((r, i) => (
                  <tr key={i}>
                    <td>{formatDDMMYYYY(r.date)}</td>
	
                    <td>{formatNumber(r.Rate)}</td>
                    <td>{r.Brand}</td>
                    <td>{r.Qty}</td>
                    <td>{formatNumber(r.BasicAmount)}</td>
                    <td>{formatNumber(r.TaxAmount)}</td>
                    <td>{formatNumber(r.AddlCessAmount)}</td>
                    <td>{formatNumber(r.ewayAmount)}</td>
                    <td>{formatNumber(r.RebateAmount)}</td>
                    <td>{formatNumber(r.GrandTotal)}</td>
                  </tr>
                ))
              )}
            </tbody>

            <tfoot>
              <tr style={{ background: "#f0f0f0", fontWeight: "bold" }}>
                <td colSpan="3" style={{ textAlign: "right" }}>TOTAL</td>
                <td>{totals.Qty}</td>
                <td>{formatNumber(totals.BasicAmount)}</td>
                <td>{formatNumber(totals.TaxAmount)}</td>
                <td>{formatNumber(totals.AddlCessAmount)}</td>
                <td>{formatNumber(totals.ewayAmount)}</td>
                <td>{formatNumber(totals.RebateAmount)}</td>
                <td>{formatNumber(totals.GrandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 12 }}>
          <button onClick={() => setPage(Math.max(1, page - 1))}>Prev</button>
          <span>Page {page}</span>
          <button onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>
    </Layout>
  );
}
